import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { Card } from '@/primer/components';
import { Button, Input } from '@/primer/components';
import { Link } from '@/primer/components';
import Typography from '@/primer/components/Typography/Typography';
import { useAuth } from '@/api';
import { signUpSchema, type SignUpFormData } from '@/schemas';
import { getFieldError } from '@/utils';
import { PageLayout } from '@/components/layout';

/**
 * SignUp — email + password registration screen.
 *
 * Social login options live on the Landing screen. This screen handles
 * credential-based account creation only.
 *
 * The Influenster for Creators logo is rendered by PreAuthLayout — this
 * component owns only the card content.
 */
export function SignUp() {
  const [submitError, setSubmitError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    } satisfies SignUpFormData,
    onSubmit: async ({ value }) => {
      setSubmitError('');
      try {
        await register(value.email, value.password);
        navigate({ to: '/settings/connect' });
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to register');
      }
    },
  });

  return (
    <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
      <Card translucent shadow="second">
        <div className="p-6 space-y-4">
          {/* Submit error */}
          {submitError && (
            <div className="bg-error border border-error rounded-small p-3">
              <Typography variant="body" size="small" color="error">
                {submitError}
              </Typography>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-4"
          >
            {/* Email */}
            <form.Field name="email" validators={{ onBlur: signUpSchema.shape.email }}>
              {(field) => (
                <Input
                  label="Email address"
                  type="email"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="you@example.com"
                  error={
                    field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined
                  }
                  autoComplete="email"
                />
              )}
            </form.Field>

            {/* Password */}
            <form.Field name="password" validators={{ onBlur: signUpSchema.shape.password }}>
              {(field) => (
                <Input
                  label="Password"
                  type="password"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="••••••••"
                  helperText="8+ characters with uppercase, lowercase, and number"
                  error={
                    field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined
                  }
                  autoComplete="new-password"
                />
              )}
            </form.Field>

            {/* Confirm password */}
            <form.Field
              name="confirmPassword"
              validators={{
                onBlur: ({ value, fieldApi }) => {
                  const password = fieldApi.form.getFieldValue('password');
                  if (!value) return 'Please confirm your password';
                  if (value !== password) return 'Passwords do not match';
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Input
                  label="Confirm Password"
                  type="password"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="••••••••"
                  error={
                    field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined
                  }
                  autoComplete="new-password"
                />
              )}
            </form.Field>

            {/* Legal copy */}
            <Typography variant="body" size="small" color="body" align="center" as="p">
              By signing up, you agree to our{' '}
              <Link to="/" variant="body" size="small">
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/" variant="body" size="small">
                Privacy Policy
              </Link>
            </Typography>

            {/* Submit */}
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" fullWidth isLoading={isSubmitting}>
                  Next
                </Button>
              )}
            </form.Subscribe>
          </form>

          {/* Navigation link */}
          <Typography variant="body" size="small" color="bodylighter" align="center" as="p">
            Already have an account?{' '}
            <Link to="/login" variant="body" size="small">
              Log in
            </Link>
          </Typography>
        </div>
      </Card>
    </PageLayout>
  );
}

export default SignUp;
